/**
 * Cross-app viewer session helpers for public profile chrome.
 *
 * SECURITY: `dronejob_viewer_*` cookies are presentation-only hints set by react-web.
 * They MUST NOT be used for authorization, Convex auth, or to expose private data.
 * Public profile data always comes from `resolvePublicProfileBySlug` (anonymous query).
 */

import { environmentSessionName } from "../../site-navigation/src/environmentSession";

const runtimeIdentity = process.env.NEXT_PUBLIC_DJ_ENVIRONMENT_ID || process.env.REACT_APP_DJ_ENVIRONMENT_ID;

import type { CanonicalMembershipType } from "@dj/convex-api";

import { isCanonicalMembershipType } from "./MembershipCrest";

/** Cookie set by react-web when a signed-in user views a public profile. */
export const viewerAuthenticatedCookieName = environmentSessionName("dronejob_viewer_authenticated", runtimeIdentity);

/** Short-lived signed grant used only for coarse pilot-activity authorization. */
export const viewerActivityGrantCookieName = environmentSessionName("dronejob_viewer_activity_grant", runtimeIdentity);

/** Presentation-only cookies for public-profile authenticated chrome (not authorization). */
export const viewerDisplayNameCookieName = environmentSessionName("dronejob_viewer_display_name", runtimeIdentity);
export const viewerAvatarUrlCookieName = environmentSessionName("dronejob_viewer_avatar_url", runtimeIdentity);
export const viewerMembershipPlanCookieName = environmentSessionName("dronejob_viewer_membership_plan", runtimeIdentity);

/** Query param that forces anonymous public-profile rendering (ignores viewer cookie). */
export const forceAnonymousViewerSearchParam = "viewer";

/** Query param used by landing-page to ask react-web to complete Auth0 logout. */
export const reactWebLogoutRequestQueryParam = "dronejobLogout";

const MAX_DISPLAY_NAME_LENGTH = 120;
const MAX_AVATAR_URL_LENGTH = 2048;

export type ViewerChromeSession = Readonly<{
  avatarUrl?: string;
  displayName: string;
  isAuthenticated: boolean;
  membershipType?: CanonicalMembershipType;
}>;

export function isViewerAuthenticatedCookieValue(value: string | undefined): boolean {
  return value === "1" || value === "true";
}

export function sanitizeViewerDisplayName(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const sanitized = value.replace(/[\u0000-\u001F\u007F]/g, "").trim();
  if (!sanitized) return undefined;
  return sanitized.slice(0, MAX_DISPLAY_NAME_LENGTH);
}

export function sanitizeViewerAvatarUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_AVATAR_URL_LENGTH) return undefined;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return undefined;
    return parsed.toString();
  } catch {
    return undefined;
  }
}

export function sanitizeViewerMembershipType(
  value: string | undefined,
): CanonicalMembershipType | undefined {
  const normalized = value?.trim().toLowerCase();
  return normalized && isCanonicalMembershipType(normalized)
    ? normalized
    : undefined;
}

export function isForceAnonymousViewerSearchParam(
  value: string | string[] | undefined,
): boolean {
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return false;
  const normalized = raw.trim().toLowerCase();
  return normalized === "anonymous" || normalized === "guest" || normalized === "public";
}

export function resolvePublicProfileViewerAuthenticated(args: Readonly<{
  cookieValue: string | undefined;
  forceAnonymousSearchParam: string | string[] | undefined;
}>): boolean {
  if (isForceAnonymousViewerSearchParam(args.forceAnonymousSearchParam)) {
    return false;
  }
  return isViewerAuthenticatedCookieValue(args.cookieValue);
}

export function resolveViewerChromeSession(args: Readonly<{
  authenticatedCookieValue: string | undefined;
  avatarUrlCookieValue: string | undefined;
  displayNameCookieValue: string | undefined;
  membershipPlanCookieValue?: string | undefined;
  forceAnonymousSearchParam?: string | string[] | undefined;
}>): ViewerChromeSession {
  const isAuthenticated = resolvePublicProfileViewerAuthenticated({
    cookieValue: args.authenticatedCookieValue,
    forceAnonymousSearchParam: args.forceAnonymousSearchParam,
  });

  if (!isAuthenticated) {
    return {
      displayName: "",
      isAuthenticated: false,
    };
  }

  return {
    displayName: sanitizeViewerDisplayName(args.displayNameCookieValue) ?? "Pilot",
    avatarUrl: sanitizeViewerAvatarUrl(args.avatarUrlCookieValue),
    isAuthenticated: true,
    membershipType: sanitizeViewerMembershipType(args.membershipPlanCookieValue),
  };
}
